import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { createCollateral, fetchBorrowers } from "../../../api/borrowers";
import { CREDORA_MODAL_CLASS, CREDORA_MODAL_CONTAINER } from "../Common/credoraModalUtils";
import { runCrud } from "../Common/credoraToast";
import BorrowerSelect from "./BorrowerSelect";
import { COLLATERAL_TYPE_OPTIONS } from "./collateralUtils";

const emptyForm = {
  borrower_id: "",
  collateral_type: "vehicle",
  description: "",
  identifier: "",
  estimated_value: "",
  ltv_ratio: "",
  location: "",
  notes: "",
};

const CollateralModal = ({ isOpen, toggle, onSaved, defaultBorrowerId = "" }) => {
  const [form, setForm] = useState({ ...emptyForm, borrower_id: defaultBorrowerId });
  const [borrowers, setBorrowers] = useState([]);
  const [loadingBorrowers, setLoadingBorrowers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setForm({ ...emptyForm, borrower_id: defaultBorrowerId });
    setError("");
    setLoadingBorrowers(true);
    fetchBorrowers(0, 200)
      .then(setBorrowers)
      .catch(() => setError("Failed to load borrowers."))
      .finally(() => setLoadingBorrowers(false));
  }, [isOpen, defaultBorrowerId]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.borrower_id) {
      setError("Select a borrower.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await runCrud(
        () =>
          createCollateral(form.borrower_id, {
            collateral_type: form.collateral_type,
            description: form.description.trim(),
            identifier: form.identifier.trim() || null,
            estimated_value: form.estimated_value,
            ltv_ratio: form.ltv_ratio || null,
            location: form.location.trim() || null,
            notes: form.notes.trim() || null,
          }),
        { successMessage: "Collateral registered successfully." }
      );
      onSaved?.();
      toggle();
    } catch {
      /* error toast shown by runCrud */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      className={CREDORA_MODAL_CLASS}
      container={CREDORA_MODAL_CONTAINER}>
      <ModalHeader toggle={toggle}>Register Collateral</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {error && <Alert color="danger">{error}</Alert>}
          <FormGroup>
            <Label for="collateral-borrower">Borrower</Label>
            {loadingBorrowers ? (
              <Spinner size="sm" />
            ) : (
              <BorrowerSelect
                inputId="collateral-borrower"
                borrowers={borrowers}
                value={form.borrower_id}
                onChange={(nextId) => updateField("borrower_id", nextId)}
                isDisabled={Boolean(defaultBorrowerId) || saving}
              />
            )}
          </FormGroup>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="collateral-type">Type</Label>
                <Input
                  id="collateral-type"
                  type="select"
                  value={form.collateral_type}
                  onChange={(e) => updateField("collateral_type", e.target.value)}>
                  {COLLATERAL_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="collateral-identifier">Identifier</Label>
                <Input
                  id="collateral-identifier"
                  value={form.identifier}
                  onChange={(e) => updateField("identifier", e.target.value)}
                  placeholder="Plate / serial / deed ref"
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label for="collateral-description">Description</Label>
            <Input
              id="collateral-description"
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              required
            />
          </FormGroup>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="collateral-value">Estimated value (TZS)</Label>
                <Input
                  id="collateral-value"
                  type="number"
                  min="1"
                  step="0.01"
                  value={form.estimated_value}
                  onChange={(e) => updateField("estimated_value", e.target.value)}
                  required
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="collateral-ltv">LTV % (optional)</Label>
                <Input
                  id="collateral-ltv"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.ltv_ratio}
                  onChange={(e) => updateField("ltv_ratio", e.target.value)}
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label for="collateral-location">Location</Label>
            <Input
              id="collateral-location"
              value={form.location}
              onChange={(e) => updateField("location", e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label for="collateral-notes">Notes</Label>
            <Input
              id="collateral-notes"
              type="textarea"
              rows="2"
              value={form.notes}
              onChange={(e) => updateField("notes", e.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" className="border" onClick={toggle} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Register Collateral"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CollateralModal;
